import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ChatCircleDots, Copy, Camera, ArrowSquareOut, PaperPlaneTilt,
  ChatCenteredDots, CheckCircle, Clock, Phone, EnvelopeSimple, Sparkle, Trash,
} from "@phosphor-icons/react";

/**
 * AskTenantPanel — appears on WO detail pages and lets the office fire a
 * magic-link message to the tenant right from the WO. If a thread has
 * already been started for this WO, the panel morphs into a live view
 * of the conversation instead of showing the "Ask" button.
 *
 * Backend contract:
 *   POST /api/jobs/{id}/tenant-request           → creates or reuses thread
 *   GET  /api/jobs/{id}/tenant-thread            → inline preview payload
 *   POST /api/jobs/{id}/tenant-thread/mark-read  → clears unread counter
 *
 * Photo mirroring: when the tenant uploads via the magic-link view, the
 * photo lands on `job.before_photos` with `source: "tenant_portal"`.
 * See routes/tenant_bridge.py tb_public_reply for the mirror logic.
 */
export default function AskTenantPanel({ jobId, job }) {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/jobs/${jobId}/tenant-thread`)
      .then(r => setThread(r.data.thread))
      .catch(() => setThread(null))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [jobId]);

  if (loading) return null;

  // No thread yet → tap-to-open button (compact so we don't clutter WOs
  // that don't need tenant input).
  if (!thread) {
    return (
      <>
        <button
          onClick={() => setShowModal(true)}
          data-testid="ask-tenant-btn"
          className="w-full h-9 mt-3 px-3 border-2 border-cyan-500 hover:bg-cyan-50 text-cyan-800 font-black uppercase text-[11px] tracking-widest rounded inline-flex items-center justify-center gap-1.5"
        >
          <ChatCircleDots weight="fill" size={14}/>
          Ask Tenant for Photos / Info
        </button>
        {showModal && (
          <AskTenantModal
            jobId={jobId}
            job={job}
            onClose={() => setShowModal(false)}
            onSent={(t) => { setShowModal(false); setThread(t); load(); }}
          />
        )}
      </>
    );
  }

  return (
    <ThreadPreview jobId={jobId} thread={thread} onRefresh={load} onReopen={() => setShowModal(true)}>
      {showModal && (
        <AskTenantModal
          jobId={jobId}
          job={job}
          onClose={() => setShowModal(false)}
          onSent={(t) => { setShowModal(false); setThread(t); load(); }}
        />
      )}
    </ThreadPreview>
  );
}


function ThreadPreview({ jobId, thread, onRefresh, onReopen, children }) {
  const unread = thread.unread_from_tenant > 0;
  const lastMsg = thread.messages?.[thread.messages.length - 1];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(thread.portal_url);
      toast.success("Portal link copied — paste into text/email");
    } catch {
      toast.error("Couldn't copy — long-press the URL to select");
    }
  };

  const markRead = async () => {
    if (!unread) return;
    try {
      await api.post(`/jobs/${jobId}/tenant-thread/mark-read`);
      onRefresh();
    } catch { /* silent */ }
  };

  const cancelRequest = async () => {
    if (!window.confirm(
      "Cancel this tenant request?\n\n" +
      "• The tenant's magic link will stop working immediately\n" +
      "• This thread will be hidden from the inbox\n" +
      "• Nothing is deleted — you can recover it via the cancelled filter"
    )) return;
    try {
      await api.post(`/tenant-bridge/threads/${thread.id}/cancel`);
      toast.success("Request cancelled — tenant link is now invalid");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail || "Cancel failed"));
    }
  };

  return (
    <div className={`mt-3 border-2 rounded-md overflow-hidden ${unread ? "border-cyan-500 shadow-lg shadow-cyan-300/40 ring-2 ring-cyan-200" : "border-slate-300 bg-white"}`}
      data-testid="ask-tenant-thread-preview">
      <div className="p-2.5 flex items-center gap-2 flex-wrap bg-slate-50 border-b border-slate-200">
        <ChatCenteredDots weight="fill" size={16} className="text-cyan-700 flex-none"/>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-600">
            Tenant Portal · {thread.tenant_display}
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            {thread.total_messages} message{thread.total_messages === 1 ? "" : "s"}
            {thread.updated_at && (
              <span className="ml-1">
                · last activity {new Date(thread.updated_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        {unread && (
          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-cyan-600 text-white rounded animate-pulse">
            {thread.unread_from_tenant} new
          </span>
        )}
        <button onClick={copyLink}
          data-testid="ask-tenant-copy-link"
          title={thread.portal_url}
          className="h-7 px-2 border border-slate-300 hover:bg-white text-slate-600 font-black uppercase text-[9px] tracking-widest rounded inline-flex items-center gap-1">
          <Copy size={10}/> Copy Link
        </button>
        <button onClick={onReopen}
          data-testid="ask-tenant-followup"
          className="h-7 px-2 border border-cyan-400 hover:bg-cyan-50 text-cyan-700 font-black uppercase text-[9px] tracking-widest rounded inline-flex items-center gap-1">
          <PaperPlaneTilt size={10}/> Follow-up
        </button>
        <Link to="/admin/tenant-bridge"
          className="h-7 px-2 border border-slate-300 hover:bg-white text-slate-700 font-black uppercase text-[9px] tracking-widest rounded inline-flex items-center gap-1">
          <ArrowSquareOut size={10}/> Full
        </Link>
        <button onClick={cancelRequest}
          data-testid="ask-tenant-cancel"
          title="Cancel — invalidates the tenant's link, hides this thread"
          className="h-7 px-2 border border-rose-300 hover:bg-rose-50 text-rose-700 font-black uppercase text-[9px] tracking-widest rounded inline-flex items-center gap-1">
          <Trash size={10}/> Cancel
        </button>
      </div>

      {/* Last message preview — most useful glance for the office. */}
      {lastMsg && (
        <div className="p-2.5 text-xs" onClick={markRead}>
          <div className="flex items-start gap-2">
            <span className={`flex-none px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${lastMsg.from === "tenant" ? "bg-cyan-100 text-cyan-800" : "bg-slate-100 text-slate-700"}`}>
              {lastMsg.from === "tenant" ? "Tenant" : "You"}
            </span>
            <div className="flex-1 min-w-0">
              {lastMsg.text && <div className="text-slate-800 leading-snug">{lastMsg.text}</div>}
              {(lastMsg.photos || []).length > 0 && (
                <div className="mt-1 flex items-center gap-1 text-[10px] text-emerald-700">
                  <Camera size={11}/> {lastMsg.photos.length} photo{lastMsg.photos.length === 1 ? "" : "s"} attached to WO
                </div>
              )}
              <div className="text-[10px] text-slate-400 mt-0.5">
                {lastMsg.at && new Date(lastMsg.at).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {children}
    </div>
  );
}


function AskTenantModal({ jobId, job, onClose, onSent }) {
  const [channel, setChannel] = useState("auto");
  const [requestType, setRequestType] = useState("photos");
  const [message, setMessage] = useState("");
  const [toPhone, setToPhone] = useState(job?.tenant_phone || "");
  const [toEmail, setToEmail] = useState(job?.tenant_email || "");
  const [busy, setBusy] = useState(false);
  const [aiDrafting, setAiDrafting] = useState(false);

  // When the admin taps "AI Diagnostic", have Claude draft a warm question
  // list tailored to the WO subject/description and drop it in the message
  // box for review. They can still edit before sending.
  const draftDiagnostic = async () => {
    setAiDrafting(true);
    try {
      const { data } = await api.post("/tenant-bridge/ai/diagnostic-draft", {
        subject: job?.title || "Maintenance issue",
        description: job?.description || "",
      });
      setMessage(data.text || "");
      if (data.fallback) toast.info("AI busy — used a template");
      else toast.success("AI draft ready — edit before sending");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail || "AI draft failed"));
    } finally { setAiDrafting(false); }
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/tenant-request`, {
        message: message.trim(),
        channel,
        to_phone: toPhone.trim(),
        to_email: toEmail.trim(),
        request_type: requestType,
      });
      const via = (data.delivered_via || []).join(" + ");
      if (via.includes("sms") || via.includes("email")) {
        toast.success(`Message sent via ${via}`);
      } else {
        toast.info("Link ready to copy — no SMS/email delivered (missing contact or key)");
      }
      onSent({ id: data.thread_id, portal_url: data.portal_url });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed to send"));
    } finally { setBusy(false); }
  };

  const templates = {
    photos: "photos of the issue",
    access_time: "your available times",
    diagnosis: "clarification on the issue",
    ai_diagnostic: "AI diagnostic Q&A",
    custom: "custom message",
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
      data-testid="ask-tenant-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-4 border-cyan-500 rounded-lg max-w-lg w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto"
        data-testid="ask-tenant-modal"
      >
        <div className="flex items-center gap-2">
          <ChatCircleDots weight="fill" size={22} className="text-cyan-600"/>
          <h2 className="text-lg font-black tracking-tight">Ask Tenant for Info</h2>
        </div>

        {/* Quick-pick request type — pre-fills the message body */}
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">What do you need?</label>
          <div className="mt-1 grid grid-cols-2 gap-1.5">
            {Object.entries(templates).map(([k, label]) => (
              <button key={k}
                onClick={() => {
                  setRequestType(k);
                  if (k === "ai_diagnostic" && !message.trim()) draftDiagnostic();
                }}
                data-testid={`ask-tenant-type-${k}`}
                className={`h-9 px-2 border-2 text-xs font-black uppercase tracking-widest rounded inline-flex items-center justify-center gap-1 ${
                  requestType === k ? "border-cyan-600 bg-cyan-50 text-cyan-800" : "border-slate-300 text-slate-500 hover:border-slate-400"
                } ${k === "ai_diagnostic" ? "bg-gradient-to-r from-fuchsia-50 to-cyan-50" : ""}`}
              >
                {k === "ai_diagnostic" && <Sparkle weight="fill" size={10} className="text-fuchsia-600"/>}
                {label}
              </button>
            ))}
          </div>
          {requestType === "ai_diagnostic" && (
            <button onClick={draftDiagnostic} disabled={aiDrafting}
              data-testid="ask-tenant-ai-redraft"
              className="mt-1.5 text-[10px] font-black uppercase tracking-widest text-fuchsia-700 hover:text-fuchsia-900 inline-flex items-center gap-1 disabled:opacity-40">
              <Sparkle weight="fill" size={10}/> {aiDrafting ? "Drafting…" : "Re-draft with AI"}
            </button>
          )}
        </div>

        {/* Custom message override — placeholder shows the template that'll auto-fire */}
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">
            Custom message {requestType !== "custom" && <span className="text-slate-400 normal-case">— optional, overrides template</span>}
          </label>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)}
            rows={3}
            data-testid="ask-tenant-message"
            placeholder={requestType === "custom"
              ? "Type your message to the tenant…"
              : `A friendly template asking for ${templates[requestType]} will be sent. Type here to override.`}
            className="mt-1 w-full px-3 py-2 border-2 border-slate-300 focus:border-cyan-500 rounded text-sm"/>
        </div>

        {/* Delivery channel — auto is the default */}
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Delivery</label>
          <div className="mt-1 grid grid-cols-4 gap-1.5">
            {[
              { k: "auto",  label: "Auto",  icon: <PaperPlaneTilt size={12}/> },
              { k: "sms",   label: "SMS",   icon: <Phone size={12}/> },
              { k: "email", label: "Email", icon: <EnvelopeSimple size={12}/> },
              { k: "url",   label: "URL",   icon: <Copy size={12}/> },
            ].map(({ k, label, icon }) => (
              <button key={k}
                onClick={() => setChannel(k)}
                data-testid={`ask-tenant-channel-${k}`}
                className={`h-9 px-2 border-2 text-[11px] font-black uppercase tracking-widest rounded inline-flex items-center justify-center gap-1 ${channel === k ? "border-cyan-600 bg-cyan-50 text-cyan-800" : "border-slate-300 text-slate-500"}`}
              >{icon}{label}</button>
            ))}
          </div>
        </div>

        {(channel === "sms" || channel === "auto") && (
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Tenant phone</label>
            <input value={toPhone} onChange={(e) => setToPhone(e.target.value)}
              data-testid="ask-tenant-phone"
              placeholder="+1 215 555 0100"
              className="mt-1 w-full h-10 px-3 border-2 border-slate-300 focus:border-cyan-500 rounded text-sm"/>
          </div>
        )}
        {(channel === "email" || channel === "auto") && (
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Tenant email</label>
            <input value={toEmail} onChange={(e) => setToEmail(e.target.value)}
              data-testid="ask-tenant-email"
              type="email"
              placeholder="tenant@example.com"
              className="mt-1 w-full h-10 px-3 border-2 border-slate-300 focus:border-cyan-500 rounded text-sm"/>
          </div>
        )}

        <div className="text-[10px] text-slate-500 leading-tight">
          Photos the tenant uploads via this link will auto-attach to this WO's <b>Before Photos</b> with source <b>tenant_portal</b>. Contact info stays firewalled — staff without owner-role sees "Tenant · Unit X" only.
        </div>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} disabled={busy}
            data-testid="ask-tenant-cancel"
            className="flex-1 h-10 border-2 border-slate-300 hover:bg-slate-100 text-slate-700 font-black uppercase text-xs tracking-widest rounded disabled:opacity-50">Cancel</button>
          <button onClick={send} disabled={busy}
            data-testid="ask-tenant-send"
            className="flex-1 h-10 bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-300 text-white font-black uppercase text-xs tracking-widest rounded inline-flex items-center justify-center gap-1.5">
            <PaperPlaneTilt weight="fill" size={13}/>
            {busy ? "Sending…" : (channel === "url" ? "Get Link" : "Send")}
          </button>
        </div>
      </div>
    </div>
  );
}
